/**
 * DemoRequests.tsx — Incoming demo requests from the landing page.
 * Rebuilt in Pass 3.
 *
 * Upgrades:
 *   - Three-up StatGrid with the "New" count as the primary (glow) stat
 *   - Inline Select per row for status updates (cleaner than the old "Change status..." dropdown)
 *   - Semantic Tag colors for status (replaces custom STATUS_COLORS rainbow)
 */

import { useEffect, useState, useCallback } from 'react';
import api from '../../api';
import { theme } from '../../theme';
import {
  PageHeader,
  Icon,
  Select,
  Table, TableToolbar, Pagination,
  Tag, TagKind,
  StatCard, StatGrid,
  EmptyState,
  toast,
} from '../../components/ui';

const STATUSES = ['new', 'contacted', 'demo_scheduled', 'converted', 'not_interested'] as const;
type Status = typeof STATUSES[number];

const STATUS_LABELS: Record<Status, string> = {
  new: 'New',
  contacted: 'Contacted',
  demo_scheduled: 'Demo scheduled',
  converted: 'Converted',
  not_interested: 'Not interested',
};

const STATUS_KINDS: Record<Status, TagKind> = {
  new: 'warning',
  contacted: 'cyan',
  demo_scheduled: 'purple',
  converted: 'success',
  not_interested: 'neutral',
};

interface DemoRequest {
  id: string;
  name: string;
  role: string;
  company: string;
  country: string;
  email: string;
  message: string;
  status: Status;
  created_at: string | null;
  updated_at: string | null;
}

const LIMIT = 20;

export default function DemoRequests() {
  const [items, setItems] = useState<DemoRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [counts, setCounts] = useState({ total: 0, new: 0, converted: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Demo Requests | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params: Record<string, string | number> = { page, limit: LIMIT };
    if (filter) params.status = filter;

    Promise.all([
      api.get('/api/demo/requests', { params }),
      api.get('/api/demo/requests', { params: { limit: 1 } }),
      api.get('/api/demo/requests', { params: { status: 'new', limit: 1 } }),
      api.get('/api/demo/requests', { params: { status: 'converted', limit: 1 } }),
    ]).then(([listR, allR, newR, convR]) => {
      setItems(listR.data.items);
      setTotal(listR.data.total);
      setCounts({
        total: allR.data.total,
        new: newR.data.total,
        converted: convR.data.total,
      });
    }).catch(() => toast.error('Failed to load demo requests'))
      .finally(() => setLoading(false));
  }, [page, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (id: string, status: string, name: string) => {
    try {
      await api.post(`/api/demo/requests/${id}/status`, { status });
      toast.success(`Updated ${name}`, `Status: ${STATUS_LABELS[status as Status] ?? status}`);
      fetchData();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const deleteRequest = async (id: string, name: string) => {
    if (!window.confirm(`Delete the demo request from ${name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/demo/requests/${id}`);
      toast.success('Demo request deleted');
      setItems(prev => prev.filter(r => r.id !== id));
      setTotal(t => Math.max(0, t - 1));
      setCounts(c => ({ ...c, total: Math.max(0, c.total - 1) }));
    } catch {
      toast.error('Failed to delete request');
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  const formatDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const columns = [
    { key: 'name', header: 'Name', render: (r: DemoRequest) => <span style={{ color: theme.text.primary }}>{r.name}</span>, nowrap: true },
    { key: 'role', header: 'Role', render: (r: DemoRequest) => <span style={{ color: theme.text.secondary, fontSize: theme.fontSize.sm }}>{r.role}</span> },
    { key: 'company', header: 'Company', render: (r: DemoRequest) => <span style={{ color: theme.text.primary, fontWeight: theme.fontWeight.medium }}>{r.company}</span> },
    { key: 'country', header: 'Country', render: (r: DemoRequest) => <span style={{ color: theme.text.secondary }}>{r.country}</span>, nowrap: true },
    {
      key: 'email',
      header: 'Email',
      render: (r: DemoRequest) => (
        <a href={`mailto:${r.email}`}
          style={{ color: theme.brand.cyanLight, textDecoration: 'none', fontSize: theme.fontSize.sm }}
          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
        >
          {r.email}
        </a>
      ),
    },
    {
      key: 'message',
      header: 'Message',
      render: (r: DemoRequest) => (
        <span
          title={r.message}
          style={{
            color: theme.text.tertiary,
            fontSize: theme.fontSize.sm,
            display: 'inline-block',
            maxWidth: 240,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'middle',
          }}
        >
          {r.message.length > 60 ? r.message.slice(0, 60) + '…' : r.message}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r: DemoRequest) => <Tag kind={STATUS_KINDS[r.status]} size="sm">{STATUS_LABELS[r.status] ?? r.status}</Tag>,
      nowrap: true,
    },
    {
      key: 'created_at',
      header: 'Date',
      render: (r: DemoRequest) => (
        <span style={{ color: theme.text.tertiary, fontFamily: theme.font.data, fontSize: theme.fontSize.sm }}>
          {formatDate(r.created_at)}
        </span>
      ),
      nowrap: true,
    },
    {
      key: 'actions',
      header: '',
      render: (r: DemoRequest) => (
        <div style={{ display: 'flex', gap: theme.space[2], alignItems: 'center' }}>
          <Select
            value=""
            onChange={e => { if (e.target.value) updateStatus(r.id, e.target.value, r.name); }}
            style={{ minWidth: 150, fontSize: theme.fontSize.sm }}
          >
            <option value="">Change status…</option>
            <option value="contacted">Mark as contacted</option>
            <option value="demo_scheduled">Schedule demo</option>
            <option value="converted">Mark as converted</option>
            <option value="not_interested">Not interested</option>
          </Select>
          <button
            type="button"
            onClick={() => deleteRequest(r.id, r.name)}
            aria-label={`Delete demo request from ${r.name}`}
            style={{
              background: theme.semantic.dangerSoft,
              color: theme.semantic.danger,
              border: `1px solid ${theme.semantic.dangerBorder}`,
              borderRadius: 6,
              padding: '4px 10px',
              cursor: 'pointer',
              fontSize: theme.fontSize.xs,
              fontWeight: theme.fontWeight.semibold,
              fontFamily: theme.font.ui,
              transition: `all ${theme.motion.fast}`,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = theme.semantic.danger; e.currentTarget.style.color = theme.text.inverse; }}
            onMouseLeave={e => { e.currentTarget.style.background = theme.semantic.dangerSoft; e.currentTarget.style.color = theme.semantic.danger; }}
          >
            Delete
          </button>
        </div>
      ),
      nowrap: true,
    },
  ];

  return (
    <div style={{ padding: `${theme.space[8]} ${theme.space[10]}`, maxWidth: 1600, margin: '0 auto' }}>
      <PageHeader
        title="Demo Requests"
        description="Incoming demo requests from the landing page."
      />

      {/* Summary cards — "New" is primary since it's the actionable count */}
      <StatGrid columns="1.4fr 1fr 1fr">
        <StatCard
          primary
          label="New · needs outreach"
          value={counts.new.toLocaleString()}
          delta={counts.new > 0 ? 'Unreviewed' : 'All caught up'}
          deltaType={counts.new > 0 ? 'down' : 'neutral'}
        />
        <StatCard
          label="Total requests"
          value={counts.total.toLocaleString()}
        />
        <StatCard
          label="Converted"
          value={counts.converted.toLocaleString()}
          delta={counts.total > 0 ? `${((counts.converted / counts.total) * 100).toFixed(1)}% of all` : '—'}
          deltaType="up"
        />
      </StatGrid>

      <div style={{ height: theme.space[6] }} />

      <div style={{ display: 'flex', gap: theme.space[3], marginBottom: theme.space[5] }}>
        <div style={{ minWidth: 200 }}>
          <Select value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }}>
            <option value="">All statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </Select>
        </div>
      </div>

      <TableToolbar>
        <div style={{ color: theme.text.tertiary, fontSize: theme.fontSize.sm }}>
          Showing{' '}
          <span style={{ color: theme.text.primary, fontFamily: theme.font.data, fontWeight: theme.fontWeight.medium }}>
            {items.length.toLocaleString()}
          </span>
          {' '}of{' '}
          <span style={{ color: theme.text.primary, fontFamily: theme.font.data, fontWeight: theme.fontWeight.medium }}>
            {total.toLocaleString()}
          </span>
        </div>
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </TableToolbar>

      <Table
        data={items}
        columns={columns}
        loading={loading}
        minWidth={1300}
        rowKey={r => r.id}
        emptyState={
          <EmptyState
            icon={<Icon.Inbox size={18} />}
            title="No demo requests yet"
            description="Requests from the landing page will appear here."
          />
        }
      />
    </div>
  );
}
